import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle, Tag, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getPriceForCurrency, formatPrice } from "../lib/api";
import { useCartStore, cartTotal } from "../store/cart";
import { useGuestCartStore, guestCartTotal, type GuestCartItem } from "../store/guestCart";
import { useCurrencyStore } from "../store/currency";
import { useAuthStore } from "../store/auth";
import { getTenantConfig } from "../lib/tenant";
import toast from "react-hot-toast";

// Curated list of shipping destinations
const COUNTRY_CODES = [
  "AU","AT","BE","BR","CA","CN","HR","CZ","DK","EG",
  "FI","FR","DE","GR","HK","HU","IN","ID","IE","IL",
  "IT","JP","JO","KE","KR","KW","LB","LU","MY","MT",
  "MX","MA","NL","NZ","NG","NO","OM","PK","PH","PL",
  "PT","QA","RO","RU","SA","SG","SK","SI","ZA","ES",
  "LK","SE","CH","TW","TH","TR","UA","AE","GB","US",
  "UY","VN","BG","BY","CY","EE","IS","LV","LT","RS",
  "BA","ME","AL","GH","ET","TZ","CO","PE","AR","CL",
  "EC","CR","PA","DO","BO","PY",
];

const JP_TAX_RATE = 0.1;

interface ShippingInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface UserProfile {
  name: string;
  shippingAddress?: string | null;
  shippingCity?: string | null;
  shippingState?: string | null;
  shippingZip?: string | null;
  shippingCountry?: string | null;
}

const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const hasPayPal = !!import.meta.env.VITE_PAYPAL_CLIENT_ID;
const hasStripe = !!stripePromise;
type PaymentMethod = "paypal" | "card" | "bank_transfer";

export default function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const isGuest = !user;
  const tenant = getTenantConfig();

  const authCart = useCartStore();
  const guestCart = useGuestCartStore();
  const { currency, siteMode } = useCurrencyStore();

  const authItems = authCart.items;
  const guestItems = guestCart.items;

  const displayItems = isGuest ? guestItems : authItems;
  const subtotal = isGuest
    ? guestCartTotal(guestItems, currency)
    : cartTotal(authItems, currency);

  if (siteMode === "read_only") {
    return (
      <main className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Checkout Disabled</h1>
        <p className="text-gray-500 mb-8">The site is currently in View-Only mode. Orders are temporarily disabled.</p>
        <Link to="/shop" className="btn-primary">Return to Shop</Link>
      </main>
    );
  }

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["user-profile"],
    queryFn: () => api.get("/auth/me").then((r) => r.data),
    enabled: !isGuest,
  });

  const [guestEmail, setGuestEmail] = useState("");
  const [shipping, setShipping] = useState<ShippingInfo>({
    name: "", address: "", city: "", state: "", zip: "", country: "US",
  });
  const [saveAddress, setSaveAddress] = useState(false);
  const [step, setStep] = useState<"shipping" | "payment">("shipping");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    hasPayPal ? "paypal" : hasStripe ? "card" : "bank_transfer"
  );
  const [confirmedOrderId, setConfirmedOrderId] = useState<string | null>(null);

  // Promotions
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{ id: string; code: string; type: string; value: number } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Country options localised to the active display language
  const countryOptions = useMemo(() => {
    try {
      const names = new Intl.DisplayNames([i18n.language], { type: "region" });
      return COUNTRY_CODES
        .map((code) => ({ code, name: names.of(code) ?? code }))
        .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
    } catch {
      return COUNTRY_CODES.map((code) => ({ code, name: code }));
    }
  }, [i18n.language]);

  // Japan consumption tax (10%)
  const isJapan = shipping.country === "JP";
  
  const promoDiscount = useMemo(() => {
    if (!appliedPromo) return 0;
    if (appliedPromo.type === "percentage") {
      return subtotal * (appliedPromo.value / 100);
    }
    if (appliedPromo.type === "fixed") {
      return appliedPromo.value;
    }
    return 0;
  }, [appliedPromo, subtotal]);

  const taxAmount = isJapan ? (subtotal - promoDiscount) * JP_TAX_RATE : 0;
  const total = subtotal - promoDiscount + taxAmount;

  async function handleValidatePromo() {
    if (!promoCode.trim() || isValidating) return;
    setIsValidating(true);
    try {
      const { data } = await api.post("/promotions/validate", { 
        code: promoCode,
        amount: subtotal
      });
      setAppliedPromo(data);
      setPromoCode("");
      toast.success("Promotion applied!");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Invalid promotion code");
    } finally {
      setIsValidating(false);
    }
  }

  useEffect(() => {
    if (profile) {
      setShipping((prev) => ({
        name: prev.name || profile.name || "",
        address: prev.address || profile.shippingAddress || "",
        city: prev.city || profile.shippingCity || "",
        state: prev.state || profile.shippingState || "",
        zip: prev.zip || profile.shippingZip || "",
        country: prev.country || profile.shippingCountry || "US",
      }));
    }
  }, [profile]);

  if (displayItems.length === 0 && !confirmedOrderId) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 text-lg mb-4">{t("checkout.emptyCart")}</p>
        <Link to="/shop" className="btn-primary">{t("checkout.browseCollection")}</Link>
      </main>
    );
  }

  if (confirmedOrderId) {
    return (
      <main className="max-w-lg mx-auto px-4 py-20 text-center space-y-5">
        <CheckCircle size={56} className="mx-auto text-green-500" />
        <h1 className="font-display text-2xl font-bold text-gray-900">{t("checkout.orderConfirmed")}</h1>
        <p className="text-gray-600">
          {t("checkout.thankYouFrom", { name: tenant.name })}{" "}
          {isGuest ? t("checkout.confirmEmail") : t("checkout.trackOrder")}
        </p>
        <p className="text-xs text-gray-400 font-mono">{t("checkout.orderRef", { id: confirmedOrderId })}</p>
        {isGuest ? (
          <Link to="/shop" className="btn-primary inline-flex">{t("checkout.continueShopping")}</Link>
        ) : (
          <Link to="/account/orders" className="btn-primary inline-flex">{t("checkout.viewOrders")}</Link>
        )}
      </main>
    );
  }

  // Resolved country label for the shipping summary
  const countryLabel = countryOptions.find((c) => c.code === shipping.country)?.name ?? shipping.country;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-3xl font-bold text-gray-900 mb-8">{t("checkout.title")}</h1>

      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-8">

          {/* ── Step 1: Shipping ──────────────────────────────────────── */}
          {step === "shipping" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setStep("payment");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="space-y-8"
            >
              {isGuest && (
                <section>
                  <h2 className="font-semibold text-gray-900 mb-4">{t("checkout.contact")}</h2>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.emailAddress")}</label>
                    <input
                      type="email"
                      className="input"
                      placeholder={t("checkout.emailPlaceholder")}
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                    />
                    <p className="mt-1.5 text-xs text-gray-400">
                      {t("checkout.emailHint")}{" "}
                      <Link to="/login" className="hover:underline" style={{ color: "rgb(var(--brand-color))" }}>{t("checkout.signIn")}</Link>{" "}
                      {t("checkout.saveOrders")}
                    </p>
                  </div>
                </section>
              )}

              <section>
                <h2 className="font-semibold text-gray-900 mb-4">{t("checkout.shipping")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.fullName")}</label>
                    <input className="input" value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} required />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.address")}</label>
                    <input className="input" value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.city")}</label>
                    <input className="input" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.stateProvince")}</label>
                    <input className="input" value={shipping.state} onChange={(e) => setShipping({ ...shipping, state: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.zipPostal")}</label>
                    <input className="input" value={shipping.zip} onChange={(e) => setShipping({ ...shipping, zip: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("checkout.country")}</label>
                    <select
                      className="input"
                      value={shipping.country}
                      onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                      required
                    >
                      {countryOptions.map(({ code, name }) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>
                  {!isGuest && (
                    <div className="sm:col-span-2 pt-1">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          style={{ accentColor: "rgb(var(--brand-color))" }}
                          checked={saveAddress}
                          onChange={(e) => setSaveAddress(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">{t("checkout.saveAddress")}</span>
                      </label>
                    </div>
                  )}
                </div>
              </section>

              <button type="submit" className="btn-primary w-full">
                {t("checkout.continueToPayment")}
              </button>
            </form>
          )}

          {/* ── Step 2: Payment ───────────────────────────────────────── */}
          {step === "payment" && (
            <div className="space-y-8">
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-gray-900">{t("checkout.shipping")}</h2>
                  <button
                    onClick={() => setStep("shipping")}
                    className="text-sm font-medium hover:underline"
                    style={{ color: "rgb(var(--brand-color))" }}
                  >
                    {t("checkout.editShipping")}
                  </button>
                </div>
                <div className="rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-600 space-y-0.5">
                  <p className="font-medium text-gray-900">{shipping.name}</p>
                  <p>{shipping.address}</p>
                  <p>{[shipping.city, shipping.state].filter(Boolean).join(", ")} {shipping.zip}</p>
                  <p>{countryLabel}</p>
                </div>
              </section>

              <section>
                <h2 className="font-semibold text-gray-900 mb-4">{t("checkout.payment")}</h2>

                {(hasPayPal || hasStripe) && (
                  <div className="mb-6 space-y-2">
                    {hasPayPal && (
                      <label
                        className="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer select-none transition-colors"
                        style={paymentMethod === "paypal" ? { borderColor: "rgb(var(--brand-color))", backgroundColor: "#f9fafb" } : { borderColor: "#e5e7eb" }}
                      >
                        <input
                          type="radio"
                          name="payment-method"
                          value="paypal"
                          checked={paymentMethod === "paypal"}
                          onChange={() => setPaymentMethod("paypal")}
                          style={{ accentColor: "rgb(var(--brand-color))" }}
                        />
                        <span className="font-medium text-gray-900">{t("checkout.payByPayPal")}</span>
                      </label>
                    )}
                    {hasStripe && (
                      <label
                        className="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer select-none transition-colors"
                        style={paymentMethod === "card" ? { borderColor: "rgb(var(--brand-color))", backgroundColor: "#f9fafb" } : { borderColor: "#e5e7eb" }}
                      >
                        <input
                          type="radio"
                          name="payment-method"
                          value="card"
                          checked={paymentMethod === "card"}
                          onChange={() => setPaymentMethod("card")}
                          style={{ accentColor: "rgb(var(--brand-color))" }}
                        />
                        <span className="font-medium text-gray-900">{t("checkout.payByCard")}</span>
                      </label>
                    )}
                    <label
                      className="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer select-none transition-colors"
                      style={paymentMethod === "bank_transfer" ? { borderColor: "rgb(var(--brand-color))", backgroundColor: "#f9fafb" } : { borderColor: "#e5e7eb" }}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        value="bank_transfer"
                        checked={paymentMethod === "bank_transfer"}
                        onChange={() => setPaymentMethod("bank_transfer")}
                        style={{ accentColor: "rgb(var(--brand-color))" }}
                      />
                      <span className="font-medium text-gray-900">{t("checkout.payByBankTransfer")}</span>
                    </label>
                  </div>
                )}

                {paymentMethod === "paypal" && hasPayPal ? (
                  <PayPalScriptProvider options={{ clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID ?? "", currency }}>
                    <PayPalCheckout
                      shipping={shipping}
                      total={total}
                      currency={currency}
                      isGuest={isGuest}
                      guestEmail={guestEmail}
                      guestItems={guestItems}
                      saveAddress={saveAddress}
                      promoId={appliedPromo?.id}
                      onSuccess={(orderId) => { guestCart.clear(); setConfirmedOrderId(orderId); }}
                    />
                  </PayPalScriptProvider>
                ) : paymentMethod === "card" && stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <StripeCheckout
                      shipping={shipping}
                      total={total}
                      currency={currency}
                      isGuest={isGuest}
                      guestEmail={guestEmail}
                      guestItems={guestItems}
                      saveAddress={saveAddress}
                      promoId={appliedPromo?.id}
                      onSuccess={(orderId) => { guestCart.clear(); setConfirmedOrderId(orderId); }}
                    />
                  </Elements>
                ) : (
                  <OfflineCheckout
                    shipping={shipping}
                    total={total}
                    currency={currency}
                    isGuest={isGuest}
                    guestEmail={guestEmail}
                    guestItems={guestItems}
                    saveAddress={saveAddress}
                    promoId={appliedPromo?.id}
                    onSuccess={(orderId) => { guestCart.clear(); setConfirmedOrderId(orderId); }}
                  />
                )}
              </section>
            </div>
          )}
        </div>

        {/* ── Order Summary sidebar ─────────────────────────────────── */}
        <aside className="bg-gray-50 rounded-2xl p-6 h-fit space-y-6">
          <div>
            <h2 className="font-semibold text-gray-900 mb-4">{t("checkout.orderSummary")}</h2>
            <ul className="space-y-3 divide-y divide-gray-100">
              {displayItems.map((item) => (
                <li key={item.id} className="flex gap-3 pt-3 first:pt-0">
                  <img src={item.product?.imageUrl || "/placeholder.jpg"} alt={item.product?.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900 line-clamp-2">{item.product?.name}</p>
                    <p className="text-gray-500">{t("checkout.qty", { count: item.quantity })}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.product ? formatPrice(getPriceForCurrency(item.product, currency) * item.quantity, currency) : ""}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Promo Code Input */}
          <div className="pt-4 border-t border-gray-200">
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Promotion Code</label>
            {appliedPromo ? (
              <div className="flex items-center justify-between bg-white border border-brand-100 rounded-xl px-3 py-2 shadow-sm">
                <div className="flex items-center gap-2 text-brand-700">
                  <Tag size={14} />
                  <span className="text-sm font-bold">{appliedPromo.code}</span>
                </div>
                <button 
                  onClick={() => setAppliedPromo(null)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter code"
                  className="input !py-2 text-sm"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && handleValidatePromo()}
                />
                <button
                  onClick={handleValidatePromo}
                  disabled={isValidating || !promoCode.trim()}
                  className="bg-gray-900 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-black transition-all disabled:opacity-50"
                >
                  {isValidating ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4 space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{t("checkout.subtotal")}</span>
              <span>{formatPrice(subtotal, currency)}</span>
            </div>
            {promoDiscount > 0 && (
              <div className="flex items-center justify-between text-sm text-green-600 font-medium">
                <span className="flex items-center gap-1.5"><Tag size={12} /> Discount</span>
                <span>-{formatPrice(promoDiscount, currency)}</span>
              </div>
            )}
            {appliedPromo?.type === "free_shipping" && (
              <div className="flex items-center justify-between text-sm text-green-600 font-medium">
                <span className="flex items-center gap-1.5"><Tag size={12} /> Free Shipping</span>
                <span>Applied</span>
              </div>
            )}
            {isJapan && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{t("checkout.consumptionTax")}</span>
                <span>{formatPrice(taxAmount, currency)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
              <span className="font-semibold text-gray-900">{t("checkout.total")}</span>
              <span className="font-bold text-xl" style={{ color: "rgb(var(--brand-color))" }}>{formatPrice(total, currency)}</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

interface PaymentProps {
  shipping: ShippingInfo;
  total: number;
  currency: string;
  isGuest: boolean;
  guestEmail: string;
  guestItems: GuestCartItem[];
  saveAddress: boolean;
  promoId?: string;
  onSuccess: (orderId: string) => void;
}

async function persistAddress(shipping: ShippingInfo) {
  await api.patch("/auth/me/address", {
    address: shipping.address,
    city: shipping.city,
    state: shipping.state,
    zip: shipping.zip,
    country: shipping.country,
  });
}

function StripeCheckout({ shipping, total, currency, isGuest, guestEmail, guestItems, saveAddress, promoId, onSuccess }: PaymentProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { clear: clearAuthCart } = useCartStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const country = shipping.country;
      const intentPayload = isGuest
        ? { currency, country, promoId, items: guestItems.map((i) => ({ productId: i.product.id, quantity: i.quantity })) }
        : { currency, country, promoId };
      const endpoint = isGuest ? "/payment/guest/stripe-intent" : "/payment/stripe/create-intent";
      const { data } = await api.post(endpoint, intentPayload);
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement)!, billing_details: { name: shipping.name } },
      });
      if (result.error) { toast.error(result.error.message ?? "Payment failed"); return; }
      if (isGuest) {
        const { data: order } = await api.post("/orders/guest", {
          items: guestItems.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
          paymentIntentId: data.intentId, paymentProvider: "stripe", shipping, guestEmail, currency, promoId,
        });
        onSuccess(order.orderId);
      } else {
        await api.post("/orders", { paymentIntentId: data.intentId, paymentProvider: "stripe", shipping, currency, promoId });
        if (saveAddress) { persistAddress(shipping).catch(() => {}); }
        await clearAuthCart();
        toast.success(t("checkout.orderPlaced"));
        navigate("/account/orders");
      }
    } catch { toast.error("Payment failed. Please try again."); } finally { setLoading(false); }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border border-gray-300 px-4 py-3">
        <CardElement options={{ style: { base: { fontSize: "15px", color: "#111827", "::placeholder": { color: "#9ca3af" } } } }} />
      </div>
      <button type="submit" disabled={loading || !stripe} className="btn-primary w-full">
        {loading ? t("checkout.processing") : t("checkout.pay", { amount: formatPrice(total, currency) })}
      </button>
    </form>
  );
}

function OfflineCheckout({ shipping, total, currency, isGuest, guestEmail, guestItems, saveAddress, promoId, onSuccess }: PaymentProps) {
  const { t } = useTranslation();
  const { clear: clearAuthCart } = useCartStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  async function handlePlaceOrder() {
    setLoading(true);
    try {
      if (isGuest) {
        const { data: order } = await api.post("/orders/guest", {
          items: guestItems.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
          paymentIntentId: null,
          paymentProvider: "offline",
          shipping,
          guestEmail,
          currency,
          promoId,
        });
        onSuccess(order.orderId);
      } else {
        await api.post("/orders", { paymentIntentId: null, paymentProvider: "offline", shipping, currency, promoId });
        if (saveAddress) { persistAddress(shipping).catch(() => {}); }
        await clearAuthCart();
        toast.success(t("checkout.orderPlaced"));
        navigate("/account/orders");
      }
    } catch {
      toast.error("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{t("checkout.offlineInfo")}</p>
      <button onClick={handlePlaceOrder} disabled={loading} className="btn-primary w-full">
        {loading ? t("checkout.processing") : `${t("checkout.placeOrder")} — ${formatPrice(total, currency)}`}
      </button>
    </div>
  );
}

function PayPalCheckout({ shipping, total, currency, isGuest, guestEmail, guestItems, saveAddress, promoId, onSuccess }: PaymentProps) {
  const { t } = useTranslation();
  const { clear: clearAuthCart } = useCartStore();
  const navigate = useNavigate();

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">{t("checkout.total")}: <strong>{formatPrice(total, currency)}</strong></p>
      <PayPalButtons
        style={{ layout: "vertical", color: "gold" }}
        createOrder={async () => {
          const country = shipping.country;
          const endpoint = isGuest ? "/payment/guest/paypal-order" : "/payment/paypal/create-order";
          const payload = isGuest
            ? { currency, country, promoId, items: guestItems.map((i) => ({ productId: i.product.id, quantity: i.quantity })) }
            : { currency, country, promoId };
          const { data } = await api.post(endpoint, payload);
          return data.id;
        }}
        onApprove={async (data) => {
          const captureEndpoint = isGuest ? `/payment/guest/paypal-capture/${data.orderID}` : `/payment/paypal/capture/${data.orderID}`;
          await api.post(captureEndpoint);
          if (isGuest) {
            const { data: order } = await api.post("/orders/guest", {
              items: guestItems.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
              paymentIntentId: data.orderID, paymentProvider: "paypal", shipping, guestEmail, currency, promoId,
            });
            onSuccess(order.orderId);
          } else {
            await api.post("/orders", { paymentIntentId: data.orderID, paymentProvider: "paypal", shipping, currency, promoId });
            if (saveAddress) { persistAddress(shipping).catch(() => {}); }
            await clearAuthCart();
            toast.success(t("checkout.orderPlaced"));
            navigate("/account/orders");
          }
        }}
        onError={() => toast.error("PayPal payment failed.")}
      />
    </div>
  );
}
